import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/** What somebody actually reaches for: a screenful, a working set, everything from one fair. */
const PAGE_SIZES = [20, 50, 100, 200] as const

/**
 * The footer of a paged list: which rows are on screen, how many fit on a page, and how to get to
 * another one.
 *
 * Jump-to-first and jump-to-last are here rather than numbered pages on purpose. A fair produces
 * hundreds of leads in three days, and a row of page numbers past about ten stops being a way to
 * navigate — narrowing the list is. What is worth one click is the other end of it.
 */
export function Pagination({
  pageIndex,
  pageCount,
  pageSize,
  range,
  onPageChange,
  onPageSizeChange,
}: {
  pageIndex: number
  pageCount: number
  pageSize: number
  /**
   * Which rows are on screen and how many there are altogether. Counted by the caller from the
   * page it actually has, not from the page it asked for: between the two there is a request in
   * flight, and a range computed from the request would name rows nobody can see.
   */
  range: { first: number; last: number; total: number }
  onPageChange: (pageIndex: number) => void
  onPageSizeChange: (size: number) => void
}) {
  const { t } = useTranslation()

  const onFirstPage = pageIndex === 0
  const onLastPage = pageIndex + 1 >= pageCount

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <p className="text-muted-foreground text-sm">{t('pagination.range', range)}</p>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {/* Not on a phone: there it pushed the page buttons onto a third line of a footer that
            stays on screen the whole time, and the default page is a phone's worth of rows. */}
        <div className="flex items-center gap-2 max-sm:hidden">
          <span className="text-muted-foreground text-sm">{t('pagination.perPage')}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(next) => next && onPageSizeChange(Number(next))}
          >
            <SelectTrigger size="sm" className="w-20" aria-label={t('pagination.perPageLabel')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.first')}
            disabled={onFirstPage}
            onClick={() => onPageChange(0)}
          >
            <ChevronsLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.previous')}
            disabled={onFirstPage}
            onClick={() => onPageChange(pageIndex - 1)}
          >
            <ChevronLeftIcon />
          </Button>
          <span className="text-muted-foreground px-2 text-sm whitespace-nowrap tabular-nums">
            {t('pagination.pageStatus', { page: pageIndex + 1, pages: pageCount })}
          </span>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.next')}
            disabled={onLastPage}
            onClick={() => onPageChange(pageIndex + 1)}
          >
            <ChevronRightIcon />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.last')}
            disabled={onLastPage}
            onClick={() => onPageChange(pageCount - 1)}
          >
            <ChevronsRightIcon />
          </Button>
        </div>
      </div>
    </div>
  )
}
